import { computed, Injectable, signal, WritableSignal } from '@angular/core';
import { ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { ModuleStructureObject } from '../../api/interfaces';

@Injectable()
export class UniqueIdService {
  private _moduleStructure: WritableSignal<ModuleStructureObject | undefined> =
    signal(undefined);

  set moduleStructure(value: WritableSignal<ModuleStructureObject>) {
    this._moduleStructure = value;
  }

  usedIds = computed(() => {
    const module = this._moduleStructure();
    if (!module) return [];
    return Object.keys(module).filter((key) => key !== 'root');
  });

  usedIdsByType = computed(() => {
    const module = this._moduleStructure();
    if (!module) return { section: [], subsection: [], widget: [] };

    const entries = Object.entries(module);
    const sections = entries
      .filter(([, value]) => value._type === 'section')
      .map(([key]) => key);
    const subsections = entries
      .filter(([, value]) => value._type === 'subsection')
      .map(([key]) => key);
    const widgets = entries
      .filter(([, value]) => value._type === 'widget')
      .map(([key]) => key);

    return {
      section: sections,
      subsection: subsections,
      widget: widgets,
    };
  });

  forbiddenIdValidator(excludedId: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      // Check if the value is included in the forbidden IDs array
      const isForbidden =
        this.usedIds().includes(value) && value !== excludedId;
      // If forbidden, return an error object, otherwise return null
      return isForbidden ? { forbiddenId: { value: value } } : null;
    };
  }

  isEmptySpace(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      // Check if the value is included in the forbidden IDs array
      const isForbidden = value.toString().replaceAll(' ', '') === '';
      // If forbidden, return an error object, otherwise return null
      return isForbidden ? { required: { value: value } } : null;
    };
  }

  idNotExistsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;
      const indexes = [];
      const ids = [];
      const matches = value.matchAll(/\$/g);
      for (const match of matches) {
        indexes.push(match.index);
      }

      for (let i = 0; i < indexes.length - 1; i += 2) {
        ids.push(value.substring(indexes[i] + 1, indexes[i + 1]));
      }

      const notExistsIds = ids.filter((id) => !this.usedIds().includes(id));

      return notExistsIds.length > 0
        ? { idNotExists: { value: notExistsIds } }
        : null;
    };
  }

  getUniqueId(
    type: 'section' | 'subsection' | 'widget',
    additionalUsedIds?: string[],
  ): string {
    const prefix = type;

    const usedIds = [...this.usedIds(), ...(additionalUsedIds ?? [])];
    const usedIdsByType = this.usedIdsByType()[type];
    let counter = Math.max(usedIdsByType.length, 0) + 1;

    let newId = `${prefix}_${counter}`;
    while (usedIds.includes(newId)) {
      counter++;
      newId = `${prefix}_${counter}`;
    }
    return newId;
  }
}
